import { NavLink } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { variant } from 'styled-system';
import { textStyles } from '../../../theme/config/typography';
import {
  StyledSideMenuLinkProps,
  StyledSideMenuProps,
} from './types';

export const StyledSideMenu = styled.div<{ $collapsed?: boolean }>`
  width: 100%;
  flex: 1;
  overflow: ${({ $collapsed }) => ($collapsed ? 'visible' : 'auto')};
`;

export const StyledSideMenuNav = styled.nav<{ $collapsed?: boolean }>`
  width: 100%;
  padding: ${({ $collapsed }) => ($collapsed ? '4px 8px' : '4px 12px')};
`;

export const StyledSideMenuEyebrow = styled.div`
  padding: var(--s-3) var(--s-2) var(--s-1);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(255, 255, 255, 0.4);
  user-select: none;
`;

export const StyledSideMenuLink = styled(NavLink).attrs({
  variant: 'body',
  activeClassName: 'is-active',
})<StyledSideMenuLinkProps>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: var(--s-2) var(--s-3);
  border-radius: var(--r-md);
  text-decoration: none;
  font-size: var(--t-md);
  font-weight: 500;
  letter-spacing: -0.005em;
  color: rgba(255, 255, 255, 0.7);
  background-color: transparent;
  gap: var(--s-3);
  margin-bottom: 2px;
  transition: background var(--dur-fast) var(--ease-out),
    color var(--dur-fast) var(--ease-out);
  position: relative;
  ${variant({ variants: { ...textStyles } })};

  ${({ $collapsed }) =>
    $collapsed &&
    css`
      justify-content: center;
      padding: var(--s-3);

      /* Badge becomes a small absolute overlay in top-right corner */
      ${StyledSideMenuBadge} {
        position: absolute;
        top: 5px;
        inset-inline-end: 5px;
        min-width: 16px;
        height: 16px;
        font-size: 9px;
        padding: 0 3px;
        border: 1.5px solid var(--brand-ink);
      }

      &::after {
        content: attr(data-label);
        position: absolute;
        inset-inline-start: calc(100% + 10px);
        top: 50%;
        transform: translateY(-50%) scale(0.96);
        background: var(--brand-ink);
        color: rgba(255, 255, 255, 0.92);
        padding: var(--s-1) var(--s-2);
        border-radius: var(--r-md);
        font-size: var(--t-sm);
        font-weight: 500;
        white-space: nowrap;
        pointer-events: none;
        opacity: 0;
        transition: opacity 140ms var(--ease-out),
          transform 140ms var(--ease-out);
        z-index: 300;
        box-shadow: 0 4px 12px rgba(9, 15, 56, 0.25);
      }

      &::before {
        content: '';
        position: absolute;
        inset-inline-start: calc(100% + 5px);
        top: 50%;
        transform: translateY(-50%);
        border: 5px solid transparent;
        border-inline-end-color: var(--brand-ink);
        pointer-events: none;
        opacity: 0;
        transition: opacity 140ms var(--ease-out);
        z-index: 300;
      }

      &:hover::after {
        opacity: 1;
        transform: translateY(-50%) scale(1);
      }

      &:hover::before {
        opacity: 1;
      }

      /* Active state in collapsed: lighter bg, lime dot at bottom */
      &.is-active {
        background-color: rgba(255, 255, 255, 0.1);
      }

      &.is-active::before {
        content: '';
        position: absolute;
        bottom: 4px;
        inset-inline-start: 50%;
        transform: translateX(-50%);
        width: 16px;
        height: 3px;
        border-radius: 2px;
        background: var(--brand-lime);
        opacity: 1;
        inset-block-start: auto;
        border: none;
      }
    `}

  &.is-active {
    background-color: var(--brand-ink-2);
    color: var(--surface);
    font-weight: 600;
  }

  &.is-active::before {
    content: '';
    position: absolute;
    inset-inline-start: 0;
    top: 8px;
    bottom: 8px;
    width: 3px;
    border-start-end-radius: 3px;
    border-end-end-radius: 3px;
    background: var(--brand-lime);
    opacity: 1;
    border: none;
    transform: none;
  }

  &.is-active ${() => StyledSideMenuIconWrapper} {
    color: var(--brand-lime);
    opacity: 1;
  }

  &:hover:not(.is-active) {
    background-color: rgba(255, 255, 255, 0.06);
    color: rgba(255, 255, 255, 0.85);
  }
`;

export const StyledSideMenuLabel = styled.span`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const StyledSideMenuList = styled.ul<StyledSideMenuProps>`
  list-style: none;
  padding: 0;
  margin: 0;
  width: 100%;
  ${({ hasMinHeight }) =>
    hasMinHeight &&
    css`
      min-height: 10rem;
    `}
`;

export const StyledSideMenuListItem = styled.li`
  width: 100%;
`;

export const StyledSideMenuBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 var(--s-1);
  border-radius: var(--r-full);
  background: rgba(215, 74, 71, 0.18);
  color: var(--danger);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 1;
  flex-shrink: 0;
`;

export const StyledSideMenuIconWrapper = styled.span<StyledSideMenuLinkProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.55);
  transition: color var(--dur-fast) var(--ease-out);

  svg { height: 18px; width: 18px; color: currentColor; }
`;
